import {
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { ListItemBox } from "lib/@components/ListItemBox"
import { NoResults } from "lib/@components/no-results/no-results"
import { Repeat } from "lib/@components/repeat"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useReferenceState } from "lib/@hooks/use-reference"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useDarkMode } from "lib/use-dark-mode"
import { useScrollRestoration } from "lib/use-scroll-restoration"
import { useState } from "react"
import { AssetEntry } from "routes/asset-register/asset-entry"
import { assetSortOptions } from "routes/asset-register/asset-register"
import { TextOnlyFieldHeader } from "routes/asset-register/plugins/regime-display/text-only-field-header"
import { getAssetsSyncedToSchedule } from "./controller/get-assets-synced-to-schedule"
import { PageSize } from "lib/@components/pagination/page-size"
import { PaginationPortal } from "lib/@components/pagination-portal"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { Bound } from "lib/@components/binding/Bound"
import { useAssetDisplayFields } from "lib/@hooks/use-asset-display-fields"
import Iconify from "minimals-template/components/Iconify"
import TableSkeleton from "minimals-template/components/table/TableSkeleton"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"

export function ScheduleAssetsTable() {
    const { target, payload } = useBoundContext()
    const scroller = useScrollRestoration()
    const isDark = useDarkMode()
    const theme = useTheme()
    const displayFields = useAssetDisplayFields()
    const [take, setTake] = useReferenceState("asset-page-size", 10)
    const [skip, setSkip] = useState(0)
    const [search] = useSearchParam("search")
    const [sort, setSort] = useState(assetSortOptions[0].value)
    const assetIds = Array.from(
        new Set(
            (payload?.assets?.length
                ? payload.assets.map((a) => a.id ?? a._id)
                : Object.values(target ?? {}).filter((value) => typeof value === "string" && value.trim().length)
            ).filter(Boolean)
        )
    )
    const { data, loading } =
        getAssetsSyncedToSchedule.useResults.status({ registers: assetIds, skip, take, search, ...sort }) ?? {}
    const { items = [], totalCount = 0 } = data ?? {}
    const isSystem = payload.assets?.length > 0

    return (
        <Bound displayFields={displayFields}>
            <Stack spacing={2} data-cy="schedule-assets-table">
                <ListItemBox spacing={2} direction="column">
                    <Typography
                        justifyContent="flex-start"
                        width="100%"
                        sx={{ mt: 1 }}
                        variant="subtitle1"
                        component="div"
                        gutterBottom
                    >
                        <If value={isSystem}>
                            System Assets
                            <Else>Assets</Else>
                        </If>
                    </Typography>
                </ListItemBox>
                <ListItemBox spacing={2}>
                    <SearchBoxUsingSearchParam
                        placeholder="Search Assets..."
                        sx={{ flex: 1, mb: 1, py: 0, "& *": { fontSize: 13 } }}
                        variant="outlined"
                        autoFocus={false}
                        startAdornment={<Iconify icon="ic:round-search" height={20} width={20} />}
                    />
                    <StandardSelector options={assetSortOptions} value={sort} onChange={setSort} />
                </ListItemBox>
                <FitInRemainingSpace
                    ref={scroller}
                    Component={TableContainer}
                    leaveSpace={NaN}
                    setHeight={true}
                    sx={{
                        "& .MuiTableCell-root": {
                            whiteSpace: "nowrap",
                            py: 0.1,
                            px: 2,
                            borderRadius: "0 !important",
                        },
                    }}
                >
                    <Table
                        size="small"
                        sx={{
                            "& .MuiTableRow-root": {
                                "&": {
                                    borderBottom: "1px solid " + theme.palette.action.hover,
                                },
                                "&:last-of-type": {
                                    borderBottom: "none",
                                },
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow
                                sx={{
                                    position: "sticky",
                                    top: 0,
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                    zIndex: 202,
                                }}
                                data-cy="assets-list-header-row"
                            >
                                <TableCell
                                    sx={{
                                        borderRadius: 0,
                                        position: "sticky",
                                        left: 0,
                                        top: 0,
                                        bgcolor: isDark ? "primary.darker" : "primary.lighter",
                                        zIndex: 201,
                                        borderRight: "1px solid",
                                        borderRightColor: "action.hover",
                                    }}
                                >
                                    Tag
                                </TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Type</TableCell>
                                <Repeat list={displayFields} item={<TextOnlyFieldHeader />} />
                            </TableRow>
                        </TableHead>

                        <RequestPageRepeat
                            portal
                            showTop
                            pageSize={take}
                            showBottom={false}
                            fallback={
                                <TableRow>
                                    <TableCell colSpan={3 + displayFields.length} sx={{ p: 0, border: "none" }}>
                                        <If value={loading}>
                                            <TableSkeleton rowCount={3} dense />
                                            <Else>
                                                <NoResults />
                                            </Else>
                                        </If>
                                    </TableCell>
                                </TableRow>
                            }
                            refreshKey={search}
                            setSkip={setSkip}
                            Component={TableBody}
                            list={items}
                            item={<AssetEntry canEdit={false} />}
                            total={totalCount}
                        />
                    </Table>
                </FitInRemainingSpace>
                <ListItemBox justifyContent="flex-end">
                    <PageSize take={take} setTake={setTake} />
                    <PaginationPortal />
                </ListItemBox>
            </Stack>
        </Bound>
    )
}
